<template>
  <div class="prod-page">
    <ProductionPageHeader
      title="Productions"
      subtitle="Every shoot line across projects — from planning through release."
    >
      <template #actions>
        <button type="button" class="move-btn-primary" @click="showForm = !showForm">
          <span class="material-icons text-lg">{{ showForm ? 'close' : 'add' }}</span>
          {{ showForm ? 'Close' : 'New production' }}
        </button>
      </template>
    </ProductionPageHeader>

    <form v-if="showForm" class="move-card prod-panel space-y-5" @submit.prevent="create">
      <ProductionSectionHead title="New production" subtitle="Attach it to a project, then name the shoot line." />
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="move-field !mb-0">
          <label for="production-project">Project</label>
          <MoveSelect
            id="production-project"
            v-model="form.project_id"
            block
            prefix-icon="folder_special"
            placeholder="Select a project"
            :options="projectOptions"
          />
        </div>
        <div class="move-field !mb-0">
          <label for="production-name">Name</label>
          <input
            id="production-name"
            v-model="form.name"
            required
            class="move-input"
            placeholder="Episode 1, Launch film…"
          />
        </div>
      </div>
      <p v-if="!projects.length" class="text-sm move-text-muted">
        No active projects yet.
        <router-link to="/production/projects" class="font-semibold" style="color: var(--move-accent)">Create a project first</router-link>
      </p>
      <p v-if="formError" class="move-alert-error">{{ formError }}</p>
      <div class="prod-form-actions">
        <button type="submit" class="move-btn-primary" :disabled="!projects.length">
          <span class="material-icons text-lg">check</span>
          Create
        </button>
        <button type="button" class="move-btn-ghost" @click="showForm = false">Cancel</button>
      </div>
    </form>

    <section class="move-board-toolbar flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div class="move-field !mb-0">
        <label for="production-status-filter" class="!text-xs !mb-1 move-text-faint uppercase tracking-wider">Status</label>
        <MoveSelect
          id="production-status-filter"
          v-model="statusFilter"
          prefix-icon="filter_list"
          size="compact"
          class="move-toolbar-select"
          :options="productionStatusFilterOptions"
          @change="load"
        />
      </div>
      <p v-if="!loading" class="text-sm move-text-faint">
        {{ items.length }} {{ items.length === 1 ? 'production' : 'productions' }}
      </p>
    </section>

    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading productions" />
    </div>
    <div v-else-if="!items.length" class="move-card move-empty-state py-16">
      <span class="material-icons">theaters</span>
      <p class="font-semibold move-text text-lg">No productions yet</p>
      <p class="text-sm move-text-faint mt-1 max-w-md">
        {{ statusFilter ? 'Nothing matches this status. Try another filter, or create a new production.' : 'Create a production under a project to start planning the shoot.' }}
      </p>
      <button v-if="!showForm" type="button" class="move-btn-primary mt-5" @click="showForm = true">
        <span class="material-icons text-lg">add</span>
        New production
      </button>
    </div>
    <div v-else class="space-y-3">
      <router-link
        v-for="item in items"
        :key="item.id"
        :to="`/production/productions/${item.id}`"
        class="prod-list-card"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-lg font-bold move-text truncate">{{ item.name }}</p>
            <p class="mt-1 text-sm move-text-muted">{{ item.project?.name }}</p>
          </div>
          <span :class="statusBadgeClass(item.status)">{{ productionStatusLabels[item.status] || item.status }}</span>
        </div>
        <p class="mt-3 text-xs move-text-faint inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <span class="inline-flex items-center gap-1">
            <span class="material-icons text-sm">event</span>
            {{ item.shoot_at ? formatDateTime(item.shoot_at) : 'Shoot day TBC' }}
          </span>
          <span v-if="item.shoot_venue" class="inline-flex items-center gap-1">
            <span class="material-icons text-sm">place</span>
            {{ item.shoot_venue }}
          </span>
        </p>
        <ProductionGaps :gaps="item.gaps" />
        <ProductionProgress class="mt-4" compact :status="item.status" />
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ProductionPageHeader from '../../components/production/ProductionPageHeader.vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import ProductionProgress from '../../components/production/ProductionProgress.vue'
import ProductionGaps from '../../components/production/ProductionGaps.vue'
import { productionApi } from '../../services/productionApi'
import {
  productionStatusFilterOptions,
  productionStatusLabels,
  statusBadgeClass
} from '../../config/productionNavigation'
import { formatDateTime } from '../../utils/productionDates'

const route = useRoute()
const loading = ref(true)
const error = ref('')
const formError = ref('')
const showForm = ref(false)
const items = ref([])
const projects = ref([])
const statusFilter = ref(typeof route.query.status === 'string' ? route.query.status : '')
const form = reactive({ project_id: '', name: '' })

const projectOptions = computed(() => projects.value.map((project) => ({
  value: String(project.id),
  label: project.name
})))

async function load () {
  loading.value = true
  error.value = ''
  try {
    const [productionsBody, projectsBody] = await Promise.all([
      productionApi.listProductions(statusFilter.value ? { status: statusFilter.value } : {}),
      productionApi.listProjects()
    ])
    items.value = productionsBody.data || []
    projects.value = (projectsBody.data || []).filter((project) => project.status !== 'archived')
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load productions.'
  } finally {
    loading.value = false
  }
}

async function create () {
  formError.value = ''
  if (!form.project_id) {
    formError.value = 'Choose a project first.'
    return
  }
  try {
    await productionApi.createProduction({ project_id: Number(form.project_id), name: form.name })
    form.name = ''
    form.project_id = ''
    showForm.value = false
    await load()
  } catch (e) {
    formError.value = e.response?.data?.message || 'Could not create production.'
  }
}

watch(() => route.query.status, (status) => {
  statusFilter.value = typeof status === 'string' ? status : ''
  load()
})

onMounted(load)
</script>
